// SNIGJ - Citizen Case Submission Page
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  FileText, Upload, Send, Clock, CheckCircle, XCircle, AlertTriangle,
  Scale, UserCheck, Heart, Key, File, Image, Video, Mic, X, Plus,
  Calendar, MapPin, Users, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { DRC_PROVINCES } from '../lib/drc-data';

const CitizenSubmissionsPage = () => {
  const { user, language } = useAuth();
  const [activeTab, setActiveTab] = useState('submit-case');
  const [submissions, setSubmissions] = useState([]);
  const [requests, setRequests] = useState({ clemency_requests: [], release_requests: [] });
  const [loading, setLoading] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState(null);
  
  // Case submission form
  const [caseForm, setCaseForm] = useState({
    title: '',
    description: '',
    case_type: '',
    category: '',
    parties_involved: [],
    accused_info: '',
    incident_date: '',
    incident_location: '',
    province: '',
    city: ''
  });
  const [caseFiles, setCaseFiles] = useState([]);
  
  // Clemency request form
  const [clemencyForm, setClemencyForm] = useState({
    beneficiary_type: 'SELF',
    beneficiary_name: '',
    beneficiary_unji: '',
    case_number: '',
    conviction_details: '',
    sentence_type: '',
    sentence_duration: '',
    time_served: '',
    justification: ''
  });
  
  // Release request form
  const [releaseForm, setReleaseForm] = useState({
    beneficiary_type: 'SELF',
    beneficiary_name: '',
    beneficiary_unji: '',
    case_number: '',
    detention_location: '',
    detention_start_date: '',
    charges: '',
    justification: '',
    guarantor_info: '',
    proposed_residence: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [submissionsRes, requestsRes] = await Promise.all([
        api.get('/citizen/my-submissions'),
        api.get('/citizen/my-requests')
      ]);
      setSubmissions(submissionsRes.data.submissions || []);
      setRequests(requestsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCase = async () => {
    try {
      // Prepare attachments info (in production, files would be uploaded first)
      const attachments = caseFiles.map(file => ({
        name: file.name,
        type: file.type,
        size: file.size
      }));
      
      const response = await api.post('/citizen/submit-case', {
        ...caseForm,
        attachments
      });
      
      setSuccessData({
        type: 'case',
        id: response.data.submission_id,
        message: response.data.message
      });
      setShowSuccessModal(true);
      
      // Reset form
      setCaseForm({
        title: '', description: '', case_type: '', category: '',
        parties_involved: [], accused_info: '', incident_date: '',
        incident_location: '', province: '', city: ''
      });
      setCaseFiles([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    }
  };

  const handleSubmitClemency = async () => {
    try {
      const response = await api.post('/citizen/clemency-request', clemencyForm);
      
      setSuccessData({
        type: 'clemency',
        id: response.data.request_id,
        fee: response.data.fee_amount,
        message: response.data.message
      });
      setShowSuccessModal(true);
      
      setClemencyForm({
        beneficiary_type: 'SELF', beneficiary_name: '', beneficiary_unji: '',
        case_number: '', conviction_details: '', sentence_type: '',
        sentence_duration: '', time_served: '', justification: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleSubmitRelease = async () => {
    try {
      const response = await api.post('/citizen/provisional-release-request', releaseForm);
      
      setSuccessData({
        type: 'release',
        id: response.data.request_id,
        fee: response.data.fee_amount,
        message: response.data.message
      });
      setShowSuccessModal(true);
      
      setReleaseForm({
        beneficiary_type: 'SELF', beneficiary_name: '', beneficiary_unji: '',
        case_number: '', detention_location: '', detention_start_date: '',
        charges: '', justification: '', guarantor_info: '', proposed_residence: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setCaseFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setCaseFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING_REVIEW: { bg: 'bg-yellow-100 text-yellow-800', icon: Clock },
      UNDER_REVIEW: { bg: 'bg-blue-100 text-blue-800', icon: Clock },
      VALIDATED: { bg: 'bg-green-100 text-green-800', icon: CheckCircle },
      REJECTED: { bg: 'bg-red-100 text-red-800', icon: XCircle },
      SUBMITTED: { bg: 'bg-blue-100 text-blue-800', icon: Send },
      APPROVED: { bg: 'bg-green-100 text-green-800', icon: CheckCircle }
    };
    const style = styles[status] || styles.PENDING_REVIEW;
    const Icon = style.icon;
    return (
      <Badge className={`${style.bg} gap-1`}>
        <Icon className="w-3 h-3" />
        {status?.replace(/_/g, ' ')}
      </Badge>
    );
  };

  const getFileIcon = (file) => {
    if (file.type?.startsWith('image/')) return <Image className="w-4 h-4 text-blue-500" />;
    if (file.type?.startsWith('video/')) return <Video className="w-4 h-4 text-purple-500" />;
    if (file.type?.startsWith('audio/')) return <Mic className="w-4 h-4 text-green-500" />;
    return <File className="w-4 h-4 text-gray-500" />;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="citizen-submissions-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            {language === 'fr' ? 'Mes Démarches Judiciaires' : 'My Legal Proceedings'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'fr' 
              ? 'Soumettez vos dossiers et demandes officielles' 
              : 'Submit your cases and official requests'}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="submit-case" className="gap-2">
              <FileText className="w-4 h-4" />
              {language === 'fr' ? 'Nouveau dossier' : 'New Case'}
            </TabsTrigger>
            <TabsTrigger value="clemency" className="gap-2">
              <Heart className="w-4 h-4" />
              {language === 'fr' ? 'Grâce présidentielle' : 'Clemency'}
            </TabsTrigger>
            <TabsTrigger value="release" className="gap-2">
              <Key className="w-4 h-4" />
              {language === 'fr' ? 'Libération provisoire' : 'Provisional Release'}
            </TabsTrigger>
            <TabsTrigger value="my-requests" className="gap-2">
              <Clock className="w-4 h-4" />
              {language === 'fr' ? 'Mes demandes' : 'My Requests'}
              {(submissions.length + requests.clemency_requests?.length + requests.release_requests?.length) > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {submissions.length + (requests.clemency_requests?.length || 0) + (requests.release_requests?.length || 0)}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Submit New Case */}
          <TabsContent value="submit-case">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {language === 'fr' ? 'Soumettre un nouveau dossier' : 'Submit a New Case'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Votre dossier sera examiné par l\'administration compétente qui lui attribuera un numéro officiel' 
                    : 'Your case will be reviewed by the competent administration who will assign an official number'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Titre du dossier' : 'Case Title'} *</Label>
                    <Input 
                      value={caseForm.title} 
                      onChange={(e) => setCaseForm({...caseForm, title: e.target.value})}
                      placeholder={language === 'fr' ? 'Ex: Litige foncier...' : 'Ex: Land dispute...'}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Type de dossier' : 'Case Type'} *</Label>
                    <Select value={caseForm.case_type} onValueChange={(v) => setCaseForm({...caseForm, case_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CRIMINAL">{language === 'fr' ? 'Pénal' : 'Criminal'}</SelectItem>
                        <SelectItem value="CIVIL">{language === 'fr' ? 'Civil' : 'Civil'}</SelectItem>
                        <SelectItem value="ADMINISTRATIVE">{language === 'fr' ? 'Administratif' : 'Administrative'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Description détaillée' : 'Detailed Description'} *</Label>
                  <Textarea 
                    value={caseForm.description} 
                    onChange={(e) => setCaseForm({...caseForm, description: e.target.value})}
                    rows={4}
                    placeholder={language === 'fr' ? 'Décrivez les faits en détail...' : 'Describe the facts in detail...'}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Date de l\'incident' : 'Incident Date'}</Label>
                    <Input 
                      type="date"
                      value={caseForm.incident_date} 
                      onChange={(e) => setCaseForm({...caseForm, incident_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Province' : 'Province'}</Label>
                    <Select value={caseForm.province} onValueChange={(v) => setCaseForm({...caseForm, province: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DRC_PROVINCES.map((p) => (
                          <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Ville/Commune' : 'City/Municipality'}</Label>
                    <Input 
                      value={caseForm.city} 
                      onChange={(e) => setCaseForm({...caseForm, city: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Lieu précis de l\'incident' : 'Exact Location'}</Label>
                  <Input 
                    value={caseForm.incident_location} 
                    onChange={(e) => setCaseForm({...caseForm, incident_location: e.target.value})}
                    placeholder={language === 'fr' ? 'Adresse ou lieu précis...' : 'Address or exact location...'}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Informations sur la partie adverse (si applicable)' : 'Opposing Party Info (if applicable)'}</Label>
                  <Textarea 
                    value={caseForm.accused_info} 
                    onChange={(e) => setCaseForm({...caseForm, accused_info: e.target.value})}
                    rows={2}
                    placeholder={language === 'fr' ? 'Nom, adresse, etc...' : 'Name, address, etc...'}
                  />
                </div>

                {/* File Upload */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    {language === 'fr' ? 'Pièces justificatives' : 'Supporting Documents'}
                  </Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    <input 
                      type="file" 
                      id="case-files"
                      multiple 
                      accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                    <label htmlFor="case-files" className="cursor-pointer">
                      <div className="flex flex-col items-center gap-2">
                        <div className="flex gap-2">
                          <Image className="w-6 h-6 text-blue-500" />
                          <Video className="w-6 h-6 text-purple-500" />
                          <Mic className="w-6 h-6 text-green-500" />
                          <File className="w-6 h-6 text-gray-500" />
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {language === 'fr' 
                            ? 'Photos, vidéos, audio, documents (PDF, Word)' 
                            : 'Photos, videos, audio, documents (PDF, Word)'}
                        </p>
                      </div>
                    </label>
                  </div>
                  
                  {caseFiles.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                      {caseFiles.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded">
                          {getFileIcon(file)}
                          <span className="text-xs truncate flex-1">{file.name}</span>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFile(index)}>
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button 
                  onClick={handleSubmitCase} 
                  className="w-full gap-2"
                  disabled={!caseForm.title || !caseForm.case_type || !caseForm.description}
                >
                  <Send className="w-4 h-4" />
                  {language === 'fr' ? 'Soumettre le dossier' : 'Submit Case'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clemency Request */}
          <TabsContent value="clemency">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  {language === 'fr' ? 'Demande de Grâce Présidentielle' : 'Presidential Clemency Request'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Soumettez une demande de grâce au Président de la République. Frais: 100 USD' 
                    : 'Submit a clemency request to the President. Fee: 100 USD'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    {language === 'fr' 
                      ? 'Cette demande sera transmise au bureau de la Présidence après validation par les services compétents.' 
                      : 'This request will be forwarded to the Presidency office after validation by competent services.'}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Bénéficiaire' : 'Beneficiary'}</Label>
                    <Select value={clemencyForm.beneficiary_type} onValueChange={(v) => setClemencyForm({...clemencyForm, beneficiary_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SELF">{language === 'fr' ? 'Moi-même' : 'Myself'}</SelectItem>
                        <SelectItem value="OTHER">{language === 'fr' ? 'Autre personne' : 'Other person'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {clemencyForm.beneficiary_type === 'OTHER' && (
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Nom du bénéficiaire' : 'Beneficiary Name'}</Label>
                      <Input 
                        value={clemencyForm.beneficiary_name} 
                        onChange={(e) => setClemencyForm({...clemencyForm, beneficiary_name: e.target.value})}
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Numéro du dossier de condamnation' : 'Conviction Case Number'}</Label>
                    <Input 
                      value={clemencyForm.case_number} 
                      onChange={(e) => setClemencyForm({...clemencyForm, case_number: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'ID National (UNJI) du condamné' : 'Convict National ID (UNJI)'}</Label>
                    <Input 
                      value={clemencyForm.beneficiary_unji} 
                      onChange={(e) => setClemencyForm({...clemencyForm, beneficiary_unji: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Détails de la condamnation' : 'Conviction Details'}</Label>
                  <Textarea 
                    value={clemencyForm.conviction_details} 
                    onChange={(e) => setClemencyForm({...clemencyForm, conviction_details: e.target.value})}
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Type de peine' : 'Sentence Type'}</Label>
                    <Select value={clemencyForm.sentence_type} onValueChange={(v) => setClemencyForm({...clemencyForm, sentence_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="IMPRISONMENT">{language === 'fr' ? 'Emprisonnement' : 'Imprisonment'}</SelectItem>
                        <SelectItem value="DEATH">{language === 'fr' ? 'Peine de mort' : 'Death Penalty'}</SelectItem>
                        <SelectItem value="LIFE">{language === 'fr' ? 'Perpétuité' : 'Life Sentence'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Durée de la peine' : 'Sentence Duration'}</Label>
                    <Input 
                      value={clemencyForm.sentence_duration} 
                      onChange={(e) => setClemencyForm({...clemencyForm, sentence_duration: e.target.value})}
                      placeholder="Ex: 10 ans"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Temps déjà purgé' : 'Time Served'}</Label>
                    <Input 
                      value={clemencyForm.time_served} 
                      onChange={(e) => setClemencyForm({...clemencyForm, time_served: e.target.value})}
                      placeholder="Ex: 5 ans"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Justification de la demande' : 'Request Justification'} *</Label>
                  <Textarea 
                    value={clemencyForm.justification} 
                    onChange={(e) => setClemencyForm({...clemencyForm, justification: e.target.value})}
                    rows={4}
                    placeholder={language === 'fr' ? 'Expliquez pourquoi vous demandez la grâce...' : 'Explain why you are requesting clemency...'}
                  />
                </div>

                <Button 
                  onClick={handleSubmitClemency} 
                  className="w-full gap-2"
                  disabled={!clemencyForm.justification}
                >
                  <Send className="w-4 h-4" />
                  {language === 'fr' ? 'Soumettre la demande de grâce (100 USD)' : 'Submit Clemency Request (100 USD)'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Provisional Release Request */}
          <TabsContent value="release">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-green-500" />
                  {language === 'fr' ? 'Demande de Libération Provisoire' : 'Provisional Release Request'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Demandez une libération provisoire en attendant le jugement. Frais: 50 USD' 
                    : 'Request provisional release pending trial. Fee: 50 USD'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Bénéficiaire' : 'Beneficiary'}</Label>
                    <Select value={releaseForm.beneficiary_type} onValueChange={(v) => setReleaseForm({...releaseForm, beneficiary_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SELF">{language === 'fr' ? 'Moi-même' : 'Myself'}</SelectItem>
                        <SelectItem value="FAMILY_MEMBER">{language === 'fr' ? 'Membre de famille' : 'Family Member'}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {releaseForm.beneficiary_type === 'FAMILY_MEMBER' && (
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Nom du détenu' : 'Detainee Name'}</Label>
                      <Input 
                        value={releaseForm.beneficiary_name} 
                        onChange={(e) => setReleaseForm({...releaseForm, beneficiary_name: e.target.value})}
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Numéro de dossier' : 'Case Number'}</Label>
                    <Input 
                      value={releaseForm.case_number} 
                      onChange={(e) => setReleaseForm({...releaseForm, case_number: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Lieu de détention' : 'Detention Location'}</Label>
                    <Input 
                      value={releaseForm.detention_location} 
                      onChange={(e) => setReleaseForm({...releaseForm, detention_location: e.target.value})}
                      placeholder={language === 'fr' ? 'Prison de Makala...' : 'Makala Prison...'}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Date de début de détention' : 'Detention Start Date'}</Label>
                    <Input 
                      type="date"
                      value={releaseForm.detention_start_date} 
                      onChange={(e) => setReleaseForm({...releaseForm, detention_start_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Charges retenues' : 'Charges'}</Label>
                    <Input 
                      value={releaseForm.charges} 
                      onChange={(e) => setReleaseForm({...releaseForm, charges: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Justification de la demande' : 'Request Justification'} *</Label>
                  <Textarea 
                    value={releaseForm.justification} 
                    onChange={(e) => setReleaseForm({...releaseForm, justification: e.target.value})}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Information sur le garant (personne se portant garante)' : 'Guarantor Information'}</Label>
                  <Textarea 
                    value={releaseForm.guarantor_info} 
                    onChange={(e) => setReleaseForm({...releaseForm, guarantor_info: e.target.value})}
                    rows={2}
                    placeholder={language === 'fr' ? 'Nom, adresse, profession, relation...' : 'Name, address, profession, relationship...'}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Adresse de résidence proposée' : 'Proposed Residence Address'}</Label>
                  <Input 
                    value={releaseForm.proposed_residence} 
                    onChange={(e) => setReleaseForm({...releaseForm, proposed_residence: e.target.value})}
                  />
                </div>

                <Button 
                  onClick={handleSubmitRelease} 
                  className="w-full gap-2"
                  disabled={!releaseForm.justification}
                >
                  <Send className="w-4 h-4" />
                  {language === 'fr' ? 'Soumettre la demande (50 USD)' : 'Submit Request (50 USD)'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Requests */}
          <TabsContent value="my-requests">
            <div className="space-y-6">
              {/* Case Submissions */}
              <Card>
                <CardHeader>
                  <CardTitle>{language === 'fr' ? 'Mes dossiers soumis' : 'My Submitted Cases'}</CardTitle>
                </CardHeader>
                <CardContent>
                  {submissions.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      {language === 'fr' ? 'Aucun dossier soumis' : 'No cases submitted'}
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {submissions.map((sub) => (
                        <div key={sub.submission_id} className="p-4 border rounded">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                {getStatusBadge(sub.status)}
                                <Badge variant="outline">{sub.case_type}</Badge>
                              </div>
                              <h4 className="font-medium">{sub.title}</h4>
                              <p className="text-sm text-muted-foreground">{sub.description?.substring(0, 100)}...</p>
                              {sub.case_number && (
                                <p className="text-sm font-mono text-primary mt-2">
                                  N° {sub.case_number}
                                </p>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {new Date(sub.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Clemency & Release Requests */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="w-5 h-5 text-red-500" />
                      {language === 'fr' ? 'Demandes de grâce' : 'Clemency Requests'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {requests.clemency_requests?.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">
                        {language === 'fr' ? 'Aucune demande' : 'No requests'}
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {requests.clemency_requests?.map((req) => (
                          <div key={req.request_id} className="p-3 border rounded">
                            <div className="flex justify-between items-start">
                              {getStatusBadge(req.status)}
                              <span className="text-xs">{new Date(req.created_at).toLocaleDateString()}</span>
                            </div>
                            <p className="text-sm mt-2 font-mono">{req.request_id}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Key className="w-5 h-5 text-green-500" />
                      {language === 'fr' ? 'Demandes de libération' : 'Release Requests'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {requests.release_requests?.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">
                        {language === 'fr' ? 'Aucune demande' : 'No requests'}
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {requests.release_requests?.map((req) => (
                          <div key={req.request_id} className="p-3 border rounded">
                            <div className="flex justify-between items-start">
                              {getStatusBadge(req.status)}
                              <span className="text-xs">{new Date(req.created_at).toLocaleDateString()}</span>
                            </div>
                            <p className="text-sm mt-2 font-mono">{req.request_id}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-6 h-6" />
              {language === 'fr' ? 'Soumission réussie' : 'Submission Successful'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p>{successData?.message}</p>
            <div className="p-4 bg-muted rounded">
              <p className="text-sm font-mono">ID: {successData?.id}</p>
              {successData?.fee && (
                <p className="text-sm mt-2">
                  {language === 'fr' ? 'Frais générés:' : 'Generated Fee:'} <strong>{successData.fee} USD</strong>
                </p>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {language === 'fr' 
                ? 'Vous recevrez une notification lorsque votre demande sera traitée.' 
                : 'You will receive a notification when your request is processed.'}
            </p>
          </div>
          <Button onClick={() => setShowSuccessModal(false)} className="w-full">
            {language === 'fr' ? 'Compris' : 'Got it'}
          </Button>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CitizenSubmissionsPage;
